import axiosInstance from "./axiosInstance";

export interface BookingReceiptTourLookup {
  id: number;
  publicId: string;
  tourCode: string;
  lawazimAmount: number;
  balanceAmount: number;
}

export interface BookingReceiptApi {
  publicId: string;
  receiptNumber: string;
  receiptDate: string;
  tourOperatorId: number;
  tourCode: string;
  receivedBy: string;
  receivedDate: string;
  paymentMode: string;
  transactionReference: string;
  remarks: string;
  description: string;
  amountReceived: number;
  status: "DRAFT" | "POSTED";
  createdAt: string;
  createdBy: number;
  updatedAt: string | null;
  updatedBy: number | null;
  postedAt: string | null;
  postedBy: number | null;
}

export interface BookingReceiptListResponse {
  success: boolean;
  message: string;
  data: {
    items: BookingReceiptApi[];
    totalCount: number;
    page: number;
    pageSize: number;
  };
  errors: unknown[];
}

export interface CreateBookingReceiptPayload {
  receiptDate: string;
  tourOperatorId: number;
  tourCode: string;
  receivedBy: string;
  receivedDate: string;
  paymentMode: string;
  transactionReference: string;
  remarks: string;
  description: string;
  amountReceived: number;
}

export interface CreateBookingReceiptResponse {
  success: boolean;
  message: string;
  data: BookingReceiptApi;
  errors: unknown[];
}

export interface BookingReceiptByIdResponse {
  success: boolean;
  message: string;
  data: BookingReceiptApi;
  errors: unknown[];
}

export interface BookingReceiptSummary {
  totalAmount: number;
  amountReceived: number;
  balanceDue: number;
}

export interface BookingReceiptSummaryResponse {
  success: boolean;
  message: string;
  data: BookingReceiptSummary;
  errors: unknown[];
}

export interface BookingReceiptByTourCodeResponse {
  success: boolean;
  message: string;
  data: BookingReceiptApi[];
  errors: unknown[];
}

export const getAuthHeader = () => {
  const token = localStorage.getItem("fhims_auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const getToursByOperator = async (
  tourOperatorId: number,
): Promise<BookingReceiptTourLookup[]> => {
  const res = await axiosInstance.get<BookingReceiptTourLookup[]>(
    "/tours/lookup",
    {
      headers: getAuthHeader(),
      params: {
        tourOperatorId,
      },
    },
  );

  return res.data;
};

export const getBookingReceipts = async (
  page = 1,
  pageSize = 20,
  tourCode = "",
  fromDate = "",
  toDate = "",
): Promise<BookingReceiptListResponse> => {
  const res = await axiosInstance.get<BookingReceiptListResponse>(
    "/tour-booking-receipts",
    {
      headers: getAuthHeader(),
      params: {
        tourCode: tourCode.trim() || undefined,
        page,
        pageSize,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      },
    },
  );

  return res.data;
};

export const createBookingReceipt = async (
  payload: CreateBookingReceiptPayload,
): Promise<CreateBookingReceiptResponse> => {
  const res = await axiosInstance.post<CreateBookingReceiptResponse>(
    "/tour-booking-receipts",
    payload,
    {
      headers: getAuthHeader(),
    },
  );

  return res.data;
};

export const postBookingReceipt = async (
  publicId: string,
): Promise<CreateBookingReceiptResponse> => {
  const res = await axiosInstance.post<CreateBookingReceiptResponse>(
    `/tour-booking-receipts/${publicId}/post`,
    {},
    {
      headers: getAuthHeader(),
    },
  );

  return res.data;
};

export const getBookingReceiptById = async (
  publicId: string,
): Promise<BookingReceiptByIdResponse> => {
  const res = await axiosInstance.get<BookingReceiptByIdResponse>(
    `/tour-booking-receipts/${publicId}`,
    {
      headers: getAuthHeader(),
    },
  );

  return res.data;
};

export const updateBookingReceipt = async (
  publicId: string,
  payload: CreateBookingReceiptPayload,
): Promise<CreateBookingReceiptResponse> => {
  const res = await axiosInstance.put<CreateBookingReceiptResponse>(
    `/tour-booking-receipts/${publicId}`,
    payload,
    {
      headers: getAuthHeader(),
    },
  );

  return res.data;
};

export const getBookingReceiptSummaryByTourCode = async (
  tourCode: string,
): Promise<BookingReceiptSummaryResponse> => {
  const res = await axiosInstance.get<BookingReceiptSummaryResponse>(
    `/tour-booking-receipts/summary/by-tour-code/${encodeURIComponent(
      tourCode,
    )}`,
    {
      headers: getAuthHeader(),
    },
  );

  return res.data;
};

export const getBookingReceiptsByTourCode = async (
  tourCode: string,
): Promise<BookingReceiptByTourCodeResponse> => {
  const res = await axiosInstance.get<BookingReceiptByTourCodeResponse>(
    `/tour-booking-receipts/by-tour-code/${encodeURIComponent(tourCode)}`,
    {
      headers: getAuthHeader(),
    },
  );

  return res.data;
};

export const downloadBookingReceiptPdf = async (
  publicId: string,
): Promise<Blob> => {
  const res = await axiosInstance.get(
    `/tour-booking-receipts/${encodeURIComponent(publicId)}/pdf`,
    {
      headers: getAuthHeader(),
      responseType: "blob",
    },
  );

  return res.data;
};
